import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useScrollLock,
  useToggle,
  useWindowSize,
  useOnClickOutside,
  useDebounceCallback,
  useTimeout,
} from 'usehooks-ts';
import I18nManager from '@/i18n';
import { ChevronDown } from 'lucide-react';
import useAppRefs from '@/hooks/useAppRefs';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import '@/components/language/language-v1.css';
import '@/components/language/language-v2.css';
import '@/components/language/right-to-left.scss';

// TODO: Convert to TS and move to header as part of compound API

const lang = new I18nManager();

export default function Language() {
  const config = useAppSelector(ConfigSlice.selectors.config);
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const { headerRef } = useAppRefs();
  if (!window.isInternationalizationOn && !config.isInternationalizationOn) return null;
  // TODO: window.isInternationalizationOn here and in other places are for a/b purpose. Once a/b is done need to remove it from every place
  if (lang.languages.length === 0) return null;

  const dropdownRef = useRef(null);
  const { i18n } = useTranslation();
  const { width = 0 } = useWindowSize();
  const { lock, unlock } = useScrollLock({ lockTarget: 'body', autoLock: false });
  const [isOpened, toggleIsOpened] = useToggle(false);
  const [currentLanguage, setCurrentLanguage] = useState(lang.getLanguage());
  const [headerHeight, setHeaderHeight] = useState(0);
  const debouncedSetHeaderHeight = useDebounceCallback(setHeaderHeight, 250);

  async function selectLanguage(lang) {
    await i18n.changeLanguage(lang);
    toggleIsOpened();
  }

  function clickOutsideDropdown() {
    if (isOpened) toggleIsOpened();
  }

  function getHeaderHeight() {
    if (headerRef.current) {
      return headerRef.current.getBoundingClientRect().height;
    } else {
      return 0;
    }
  }

  useOnClickOutside(dropdownRef, clickOutsideDropdown);

  useTimeout(() => debouncedSetHeaderHeight(getHeaderHeight()), 250);

  useEffect(() => {
    const root = document.getElementById('root');
    const newLang = lang.getLanguage();
    root.setAttribute('data-direction', lang.getDirection(newLang.key));
    root.setAttribute('data-language', newLang.key);
    setCurrentLanguage(newLang);
  }, [i18n.language]);

  useEffect(() => (isOpened ? lock() : unlock()), [isOpened]);

  useEffect(() => debouncedSetHeaderHeight(getHeaderHeight()), [width]);

  return (
    <div ref={dropdownRef} data-css-version={cssVersion} className='language'>
      <button className='language__current' onClick={toggleIsOpened}>
        <i className='language__flag'>{currentLanguage.flag}</i>
        <span className='language__country language__country--current'>{currentLanguage.name}</span>
        <ChevronDown className='language__arrow' data-is-opned={isOpened} />
      </button>
      <div className='language__dropdown' data-is-open={isOpened} style={{ top: headerHeight }}>
        <ul className='language__list'>
          {lang.languages.map((language) => (
            <li key={language.key} className='language__item'>
              <button type='button' className='language__button' onClick={() => selectLanguage(language.key)}>
                <i className='language__flag'>{language.flag}</i>
                <span className='language__country'>{language.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
